import along from "@turf/along";
import { Feature, LineString, Position } from "geojson";
import geojsonRbush, { RBush } from "geojson-rbush";

import { bbox, distance, length, nearestPointOnLine } from "fond/turf";

import { EndpointIndex, findFeaturesAtEndpoint, isCrossing } from "./chainSelection";
import { METERS_PER_DEG_LAT } from "./constants";

export type RoadSide = "north" | "south" | "east" | "west";

export interface RoadSideClassification {
  orientation: "ns" | "ew";
  /** "east" for NS road, "north" for EW road */
  sideA: RoadSide;
  /** "west" for NS road, "south" for EW road */
  sideB: RoadSide;
  sideAIds: Set<string>;
  sideBIds: Set<string>;
  crossingIds: Set<string>;
}

/** Default distance (meters) a sample point can be from the road to count as "near" */
const DEFAULT_DISTANCE_THRESHOLD = 25;

/** Default fraction of sample points that must be near the road */
const DEFAULT_COVERAGE_THRESHOLD = 0.5;

/** Maximum angle difference (degrees) between feature and road to count as parallel */
const MAX_ANGLE_DIFF = 45;

/** Maximum number of sample points per feature to cap nearestPointOnLine cost */
const MAX_SAMPLE_COUNT = 50;

interface FeatureIdProperties {
  featureId: string;
}

export interface RoadSelectionIndex {
  tree: RBush<LineString, FeatureIdProperties>;
  featureMap: Map<string, Feature<LineString>>;
}

export interface ParallelOptions {
  /** Distance in meters — how close a sample point must be to the road (default 25) */
  distanceThreshold?: number;
  /** Fraction 0–1 — what share of samples must be within the threshold (default 0.5) */
  coverageThreshold?: number;
}

/**
 * Compute the bearing of a specific segment within a coordinate array.
 */
function segmentBearing(coords: Position[], index: number): number {
  const safeIndex = Math.min(index, coords.length - 2);
  const [lng1, lat1] = coords[safeIndex];
  const [lng2, lat2] = coords[safeIndex + 1];
  return Math.atan2(lng2 - lng1, lat2 - lat1) * (180 / Math.PI);
}

/**
 * Compute the absolute angle difference between two bearings,
 * normalized to 0–90° (since parallel lines can point in opposite directions).
 */
function angleDiff(b1: number, b2: number): number {
  let diff = Math.abs(b1 - b2) % 360;
  if (diff > 180) diff = 360 - diff;
  if (diff > 90) diff = 180 - diff;
  return diff;
}

/**
 * Find which segment of the coordinate array a given distance (meters) falls on.
 * Returns the segment index (0-based).
 */
function segmentIndexAtDistance(coords: Position[], targetDist: number): number {
  let cumulative = 0;
  for (let i = 0; i < coords.length - 1; i++) {
    const segLen = distance(coords[i], coords[i + 1], { units: "meters" });
    cumulative += segLen;
    if (cumulative >= targetDist) return i;
  }
  return Math.max(0, coords.length - 2);
}

/**
 * Builds a spatial index of all LineString draw features.
 * Non-LineString features and features without an id are skipped.
 */
export function buildFeatureBBoxIndex(features: Feature[]): RoadSelectionIndex {
  const tree = geojsonRbush<LineString, FeatureIdProperties>();
  const featureMap = new Map<string, Feature<LineString>>();
  const indexable: Array<Feature<LineString, FeatureIdProperties>> = [];

  for (const feature of features) {
    if (feature.geometry?.type !== "LineString") continue;

    const featureId = String(feature.id ?? feature.properties?.id ?? "");
    if (!featureId) continue;

    const coords = (feature.geometry as LineString).coordinates;
    if (!coords || coords.length < 2) continue;

    const ls = feature as Feature<LineString>;
    featureMap.set(featureId, ls);

    // Wrap with featureId property so we can look up the original after search
    indexable.push({
      ...ls,
      properties: { ...ls.properties, featureId } as FeatureIdProperties & Record<string, unknown>,
    } as Feature<LineString, FeatureIdProperties>);
  }

  tree.load(indexable);
  return { tree, featureMap };
}

/**
 * Given a road LineString, finds all draw features in the index that run
 * parallel to it (based on per-sample distance + angle checks).
 *
 * For each candidate feature near the road:
 *   1. Sample evenly-spaced points along the feature
 *   2. For each sample, check both proximity AND bearing alignment to the road
 *   3. A sample is a "hit" only if within distanceThreshold AND angle < MAX_ANGLE_DIFF
 *   4. If ≥ coverageThreshold of samples are hits → feature is parallel
 */
export function findParallelFeatures(index: RoadSelectionIndex, road: Feature<LineString>, options?: ParallelOptions): Set<string> {
  const distanceThreshold = options?.distanceThreshold ?? DEFAULT_DISTANCE_THRESHOLD;
  const coverageThreshold = options?.coverageThreshold ?? DEFAULT_COVERAGE_THRESHOLD;
  const result = new Set<string>();
  const roadCoords = road.geometry.coordinates;

  // Expand the road's bbox by the distance threshold, accounting for latitude
  const roadBbox = bbox(road) as [number, number, number, number];
  const midLat = (roadBbox[1] + roadBbox[3]) / 2;
  const latPad = distanceThreshold / METERS_PER_DEG_LAT;
  const lngPad = distanceThreshold / (METERS_PER_DEG_LAT * Math.cos((midLat * Math.PI) / 180));
  const searchBbox: [number, number, number, number] = [roadBbox[0] - lngPad, roadBbox[1] - latPad, roadBbox[2] + lngPad, roadBbox[3] + latPad];

  const candidates = index.tree.search(searchBbox);

  for (const candidate of candidates.features) {
    const featureId = candidate.properties?.featureId;
    if (!featureId) continue;

    const original = index.featureMap.get(featureId);
    if (!original) continue;

    // Skip crossings — they'll be picked up in the second pass
    if (isCrossing(original)) continue;

    const lengthM = length(original, { units: "meters" });
    if (lengthM < 1) continue;

    const featureCoords = original.geometry.coordinates;
    const sampleCount = Math.min(MAX_SAMPLE_COUNT, Math.max(5, Math.ceil(lengthM / 10)));
    let hits = 0;
    const debugSamples: string[] = [];

    for (let i = 0; i < sampleCount; i++) {
      const dist = sampleCount === 1 ? 0 : (i / (sampleCount - 1)) * lengthM;
      const samplePoint = along(original, dist, { units: "meters" });
      const nearest = nearestPointOnLine(road, samplePoint, { units: "meters" });
      const d = nearest.properties.dist ?? Infinity;

      // Per-sample angle: compare the feature's local bearing to the road's bearing
      const featSegIdx = segmentIndexAtDistance(featureCoords, dist);
      const roadSegIdx = nearest.properties.index ?? 0;
      const featBearing = segmentBearing(featureCoords, featSegIdx);
      const roadBearing = segmentBearing(roadCoords, roadSegIdx);
      const angle = angleDiff(featBearing, roadBearing);

      const nearEnough = d <= distanceThreshold;
      const aligned = angle <= MAX_ANGLE_DIFF;
      const isHit = nearEnough && aligned;
      if (isHit) hits += 1;

      debugSamples.push(`${Math.round(d)}m/${Math.round(angle)}°${isHit ? "✓" : ""}`);
    }

    const coverage = hits / sampleCount;
    const selected = coverage >= coverageThreshold;

    console.warn(`[roadSelection]   ${selected ? "✓" : "✗"} ${featureId}`, {
      lengthM: Math.round(lengthM),
      sampleCount,
      hits,
      coverage: `${(coverage * 100).toFixed(0)}%`,
      samples: debugSamples,
    });

    if (selected) {
      result.add(featureId);
    }
  }

  console.warn(`[roadSelection] result: ${result.size} features selected`, Array.from(result));
  return result;
}

/**
 * Given a set of parallel feature IDs, finds all crossings connected to them
 * via co-located endpoints (using the same endpoint index as chain selection).
 */
export function findConnectedCrossings(endpointIndex: EndpointIndex, parallelIds: Set<string>): Set<string> {
  const crossingIds = new Set<string>();

  for (const featureId of parallelIds) {
    const feature = endpointIndex.featureMap.get(featureId);
    if (!feature) continue;

    const coords = feature.geometry.coordinates;
    const endpoints = [coords[0], coords[coords.length - 1]];

    for (const endpoint of endpoints) {
      const neighborIds = findFeaturesAtEndpoint(endpointIndex.tree, endpoint);
      for (const neighborId of neighborIds) {
        if (parallelIds.has(neighborId) || crossingIds.has(neighborId)) continue;
        const neighbor = endpointIndex.featureMap.get(neighborId);
        if (neighbor && isCrossing(neighbor)) {
          crossingIds.add(neighborId);
        }
      }
    }
  }

  console.warn(`[roadSelection] crossings: ${crossingIds.size} found`, Array.from(crossingIds));
  return crossingIds;
}

/**
 * Computes the overall orientation of a road: "ns" (North–South) or "ew" (East–West).
 *
 * Uses length-weighted circular averaging with the doubled-angle technique
 * so that opposite directions (N vs S) collapse to the same orientation.
 */
export function computeRoadOrientation(road: Feature<LineString>): "ns" | "ew" {
  const coords = road.geometry.coordinates;
  let sinSum = 0;
  let cosSum = 0;

  for (let i = 0; i < coords.length - 1; i++) {
    const bearing = segmentBearing(coords, i);
    // Double the angle to handle 180° ambiguity
    const rad = ((bearing * Math.PI) / 180) * 2;
    const segLen = distance(coords[i], coords[i + 1], { units: "meters" });
    sinSum += Math.sin(rad) * segLen;
    cosSum += Math.cos(rad) * segLen;
  }

  const avgBearing = (Math.atan2(sinSum, cosSum) * 180) / Math.PI / 2;
  // Normalize to 0–180
  const normalized = ((avgBearing % 180) + 180) % 180;

  // 0–45° or 135–180° → NS road (sides are east/west)
  // 45–135° → EW road (sides are north/south)
  return normalized <= 45 || normalized >= 135 ? "ns" : "ew";
}

/**
 * Classifies each parallel feature by which side of the road it falls on.
 *
 * For a NS road, features are split into east/west by comparing the feature
 * midpoint's longitude to the nearest road point's longitude.
 * For an EW road, they are split into north/south by comparing latitudes.
 *
 * Crossings are not assigned to either side — they are passed through as-is.
 *
 * If `orientationOverride` is provided, it is used instead of computing
 * the road's own orientation. This allows shift-click to classify new
 * features using the same reference frame as the first road.
 */
export function classifyFeaturesBySide(
  index: RoadSelectionIndex,
  road: Feature<LineString>,
  parallelIds: Set<string>,
  crossingIds: Set<string>,
  orientationOverride?: "ns" | "ew"
): RoadSideClassification {
  const orientation = orientationOverride ?? computeRoadOrientation(road);

  const sideA: RoadSide = orientation === "ns" ? "east" : "north";
  const sideB: RoadSide = orientation === "ns" ? "west" : "south";

  const sideAIds = new Set<string>();
  const sideBIds = new Set<string>();

  for (const featureId of parallelIds) {
    const feature = index.featureMap.get(featureId);
    if (!feature) continue;

    const featureLength = length(feature, { units: "meters" });
    const mid = along(feature, featureLength / 2, { units: "meters" });
    const nearest = nearestPointOnLine(road, mid, { units: "meters" });

    const [midLng, midLat] = mid.geometry.coordinates;
    const [nearLng, nearLat] = nearest.geometry.coordinates;

    // Classify by comparing the feature midpoint to the nearest road point
    const isSideA =
      orientation === "ns"
        ? midLng >= nearLng // east of road
        : midLat >= nearLat; // north of road

    if (isSideA) {
      sideAIds.add(featureId);
    } else {
      sideBIds.add(featureId);
    }
  }

  return { orientation, sideA, sideB, sideAIds, sideBIds, crossingIds };
}
